import type { RfiRequest } from 'app/modules/rfi/responses';
import type { RfiDetailsPageType, RfiStatus } from 'app/modules/rfi/models';

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  webm: 'video/webm',
};

export function mimeFromFilename(
  filename: string | null | undefined,
): string | null {
  if (!filename) {
    return null;
  }
  const ext = filename.split('.').pop()?.toLowerCase();
  return (ext && MIME_BY_EXT[ext]) ?? null;
}

export function blobUrlFromBase64(
  contentBase64: string,
  mime: string | null,
): string {
  const bin = atob(contentBase64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime ?? undefined });
  return URL.createObjectURL(blob);
}

export const getRfiRequestsUrl = (
  detailsPageType: RfiDetailsPageType,
  id: string,
  hashedId?: string,
): string => {
  // Entity/instrument ids are external_ids, which can contain URL-unsafe
  // characters, so they must be encoded. hashedId is alphanumeric
  // ({org_id}{56 hex}), so it never needs encoding. The article path takes a
  // case/alert article id (not an external_id) whose ':' is structural — leave
  // it raw.
  if (detailsPageType === 'ENTITY') {
    return hashedId
      ? `/v1/rfi/requests/by-entity/cached/${hashedId}`
      : `/v1/rfi/requests/by-entity/${encodeURIComponent(id)}`;
  }
  if (detailsPageType === 'INSTRUMENT') {
    return hashedId
      ? `/v1/rfi/requests/by-instrument/cached/${hashedId}`
      : `/v1/rfi/requests/by-instrument/${encodeURIComponent(id)}`;
  }
  return `/v1/rfi/requests/by-article/${id}`;
};

export const getGlobalRfiStatus = (statuses: RfiStatus[]): RfiStatus | null => {
  if (statuses.length === 0) {
    return null;
  }
  if (statuses.includes('submitted')) {
    return 'submitted';
  }
  if (statuses.includes('expired')) {
    return 'expired';
  }
  if (statuses.includes('deleted')) {
    return 'deleted';
  }
  return 'pending';
};

export const getRfiStatus = (request: RfiRequest): RfiStatus => {
  if (request.deleted_at) {
    return 'deleted';
  }
  if (request.has_response) {
    return 'submitted';
  }
  if (request.deadline && new Date(request.deadline) <= new Date()) {
    return 'expired';
  }
  return 'pending';
};
